import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export type OfficeFileType = 'docx' | 'xlsx' | 'pptx';

interface Props {
  open: boolean;
  onConfirm: (fileType: OfficeFileType, filename: string) => Promise<void> | void;
  onClose: () => void;
}

export default function NewOfficeFileDialog({ open, onConfirm, onClose }: Props) {
  const { t } = useTranslation();
  const [fileType, setFileType] = useState<OfficeFileType>('docx');
  const [filename, setFilename] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setFilename(''); setFileType('docx'); setSaving(false); };
  const handleClose = () => { reset(); onClose(); };

  const handleConfirm = async () => {
    const trimmed = filename.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try { await onConfirm(fileType, trimmed); reset(); onClose(); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('myDrive.newOfficeFile', '新增 Office 檔案')}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <FormControl fullWidth size="small" sx={{ mt: 1 }}>
          <InputLabel>{t('myDrive.type', '類型')}</InputLabel>
          <Select label={t('myDrive.type', '類型')} value={fileType}
                  onChange={e => setFileType(e.target.value as OfficeFileType)}>
            <MenuItem value="docx">{t('myDrive.wordDocument', 'Word 文件 (.docx)')}</MenuItem>
            <MenuItem value="xlsx">{t('myDrive.excelSpreadsheet', 'Excel 試算表 (.xlsx)')}</MenuItem>
            <MenuItem value="pptx">{t('myDrive.powerpointPresentation', 'PowerPoint 簡報 (.pptx)')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          autoFocus fullWidth size="small"
          label={t('myDrive.officeFilenameLabel', '檔名（不含副檔名）')}
          value={filename}
          onChange={e => setFilename(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleConfirm()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel', '取消')}</Button>
        <Button variant="contained" onClick={handleConfirm} disabled={!filename.trim() || saving}>
          {t('common.create', '建立')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
